/**
 * /blog/category/:category — posts filtered by a single category.
 *
 * Uses the same card layout as the main blog list. Server-side filters
 * the rolling 90-day window via ``?category=<slug>``. Unknown category
 * slugs degrade to an empty state (not an error) — the backend returns
 * ``[]`` and the UI shows a friendly "no posts yet" message.
 */

import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Star, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import useSeo from "@/lib/useSeo";
import useFaqPageJsonLd from "@/lib/useFaqPageJsonLd";
import BlogFaqSection from "@/components/blog/BlogFaqSection";
import MedicareStickyCta, {
    MedicareCtaSentinel,
} from "@/components/blog/MedicareStickyCta";
import { CATEGORY_META } from "@/lib/blogCategories";

const API = "/api";

export default function BlogCategoryPage() {
    const { category } = useParams();
    const meta = CATEGORY_META[category];
    useSeo({
        title: meta?.seoTitle,
        description: meta?.seoDescription,
        canonicalPath: category ? `/blog/category/${category}` : undefined,
    });
    // Inject FAQPage JSON-LD only when the category meta authored a
    // `faqs` array. Scoped node id so it doesn't collide with the
    // post-level FAQPage schema on /blog/:slug.
    useFaqPageJsonLd(meta?.faqs, { nodeId: "ldjson-faqpage-category" });

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    // State-backed ref (not useRef) for the sentinel DOM node — using
    // useState ensures the MedicareStickyCta re-renders when the node
    // is finally mounted (after async post-fetch resolves) so its
    // IntersectionObserver effect runs against a real element.
    const [sentinelNode, setSentinelNode] = useState(null);

    useEffect(() => {
        if (!category) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios
            .get(`${API}/blog/posts`, {
                headers: { "Cache-Control": "no-cache" },
                params: { category, _: Date.now() },
            })
            .then((res) => {
                if (cancelled) return;
                setPosts(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                if (cancelled) return;
                setError(
                    "We couldn't reach the blog feed. Please retry in a moment."
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [category, retryCount]);

    // Unknown category → bounce to /blog (preserve old shared links).
    if (!meta) return <Navigate to="/blog" replace />;

    return (
        <main
            data-testid={`blog-category-page-${category}`}
            className="flex min-h-screen flex-col bg-brand-bg text-brand-ivory"
        >
            <PageHeader />
            <section className="flex-1 px-5 py-12 sm:px-8 md:py-16">
                <div className="mx-auto max-w-4xl">
                    <Link
                        to="/blog"
                        data-testid="blog-category-back"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-gold2 hover:text-brand-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        All categories
                    </Link>

                    <p className="mt-6 text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Agency Blog · {meta.shortLabel || meta.label}
                    </p>
                    <h1
                        data-testid="blog-category-title"
                        className="mt-3 font-serif text-4xl leading-tight text-brand-ivory sm:text-5xl"
                    >
                        {meta.seoH1 || meta.label}
                    </h1>
                    <div className="gold-hairline my-6 w-32" />
                    <p className="max-w-2xl text-base leading-relaxed text-brand-ivory/85">
                        {meta.tagline}
                    </p>

                    {meta.intro?.length ? (
                        <div
                            data-testid="blog-category-intro"
                            className="mt-6 max-w-3xl space-y-4 text-sm leading-relaxed text-brand-ivory/80 sm:text-base"
                        >
                            {meta.intro.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    ) : null}

                    {loading ? (
                        <div className="mt-12 flex items-center gap-3 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-8 text-sm text-brand-muted">
                            <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                            Loading…
                        </div>
                    ) : error ? (
                        <div
                            data-testid="blog-category-error"
                            className="mt-12 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-200"
                        >
                            <p>{error}</p>
                            <button
                                type="button"
                                onClick={() => {
                                    setLoading(true);
                                    setError(null);
                                    setRetryCount((n) => n + 1);
                                }}
                                data-testid="blog-category-retry"
                                className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-300/50 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-rose-500/20"
                            >
                                Retry
                            </button>
                        </div>
                    ) : posts.length === 0 ? (
                        <div
                            data-testid="blog-category-empty"
                            className="mt-12 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-8 text-sm text-brand-muted"
                        >
                            No posts in this category yet. We&apos;re working on
                            it — check back soon.
                        </div>
                    ) : (
                        <ul
                            className="mt-12 space-y-6"
                            data-testid="blog-category-list"
                        >
                            {posts.map((post, idx) => (
                                <React.Fragment key={post.slug}>
                                    <li>
                                    <Link
                                        to={`/blog/${post.slug}`}
                                        data-testid={`blog-card-${post.slug}`}
                                        className="group block overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] transition-colors hover:border-[rgba(212,175,55,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                                    >
                                        <div className="grid grid-cols-1 gap-0 md:grid-cols-[1fr_2fr]">
                                            {post.hero_image_url ? (
                                                <div className="aspect-[16/10] overflow-hidden bg-black md:aspect-auto">
                                                    <img
                                                        src={post.hero_image_url}
                                                        alt={`${post.title} hero`}
                                                        loading="lazy"
                                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="aspect-[16/10] bg-gradient-to-br from-[#0b0e13] via-[#101620] to-[#0b0e13] md:aspect-auto" />
                                            )}
                                            <div className="p-6 sm:p-8">
                                                {post.is_sticky ? (
                                                    <span
                                                        data-testid={`blog-card-featured-${post.slug}`}
                                                        className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.12)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.3em] text-brand-gold"
                                                    >
                                                        <Star className="h-3 w-3 fill-current" />
                                                        Featured
                                                    </span>
                                                ) : null}
                                                <h2 className="mt-3 font-serif text-2xl text-brand-ivory sm:text-3xl">
                                                    {post.title}
                                                </h2>
                                                <p className="mt-3 line-clamp-3 text-sm text-brand-muted sm:text-base">
                                                    {post.summary}
                                                </p>
                                                <p className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-brand-gold2">
                                                    Read post
                                                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                                </p>
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                                {/* Sentinel for the Medicare sticky CTA. Lives
                                    after the 3rd post on /health only. */}
                                {category === "health" && idx === 2 ? (
                                    <MedicareCtaSentinel setNode={setSentinelNode} />
                                ) : null}
                                </React.Fragment>
                            ))}
                        </ul>
                    )}

                    {meta.faqs && meta.faqs.length > 0 ? (
                        <BlogFaqSection
                            faqs={meta.faqs}
                            testid="blog-category-faq"
                            eyebrow={`${meta.shortLabel || meta.label} · FAQ`}
                            heading={meta.faqHeading || "Frequently asked questions."}
                        />
                    ) : null}
                </div>
            </section>
            <FooterBar />
            {category === "health" ? (
                <MedicareStickyCta sentinelNode={sentinelNode} />
            ) : null}
        </main>
    );
}
